import { MessageBusService, MessageTypeEnum } from "@/shared/message-bus";
import { CellClickedEvent, GridReadyEvent } from "@ag-grid-community/core";
import { Location } from "@angular/common";
import {
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { EntityIssue, JoNote, PoNote } from "app/model/entities/entity-model";
import {
  AgFns,
  GridOptionsExt,
  ISortModel,
  RowSelectionMultipleCheckbox,
} from "app/shared/ag-fns";
import { AgStyleFns } from "app/shared/ag-style-fns";
import {
  DomainBaseComponent,
  wrapBusy,
} from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { NavFns } from "app/shared/nav-fns";
import { LinkHelpTooltipComponent } from "app/shared/tooltip.component";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "entity-issue",
  templateUrl: "./entity-issue.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class EntityIssueComponent
  extends DomainBaseComponent
  implements OnInit {
  @ViewChild("eiButtonsCell") eiButtonsCell: TemplateRef<any>;

  eiGridOptions: GridOptionsExt;
  entityIssues: EntityIssue[];

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,

    protected location: Location,
    protected messageBus: MessageBusService
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.eiGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onEiGridReady,
      rowSelection: { ...RowSelectionMultipleCheckbox, headerCheckbox: true },
      onModelUpdated: (p) => { }, // need to suppress automatic autosize that occurs here.
      getRowHeight: undefined, // // we want autoHeight for this grid
    });

    this.entityIssues = await this.dbQueryService.getEntityIssues();
    this.isPageReady = true;
  }

  async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(EntityIssue);
    return true;
  }

  async onEiGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Entity Type",
        field: "entityTypeName",
        filter: "agTextColumnFilter",
        width: 100,
      },
      {
        headerName: "Entity Id",
        field: "entityId",
        filter: "agTextColumnFilter",
        width: 100,
        cellClass: AgStyleFns.getLinkCellClass(),
        onCellClicked: (event) =>
          this.gotoIssue(event, event.data as EntityIssue),
        tooltipComponent: LinkHelpTooltipComponent,
        tooltipField: "entityId",
      },
      // { headerName: 'Length', field: 'descLength', filter: 'agTextColumnFilter' },
      {
        headerName: "Account",
        field: "account.accountName",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Description",
        field: "description",
        filter: "agTextColumnFilter",
        width: 500,
        tooltipField: "description",
        cellStyle: { "white-space": "normal" },
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Type",
        field: "warningType.name",
        filter: "agTextColumnFilter",
        width: 100,
      },
      {
        headerName: "Created By",
        field: "crtnUserInit",
        filter: "agTextColumnFilter",
        width: 100,
      },
      { headerName: "Created On", field: "crtnTs", width: 140 },
    ];

    const sortModel = [{ colId: "crtnTs", sort: "desc" }] as ISortModel;

    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.sizeColumnsToFit(this.eiGridOptions);

    this.eiGridOptions.apiX.resetRowHeights();
  }

  async gotoIssue(event: CellClickedEvent, issue: EntityIssue) {
    if (issue.entityId == null) return;
    if (issue.entityTypeName === "JoHeader") {
      this.gotoNavPath(event, "/in-process/manage/job-orders", issue.entityId);
    } else if (issue.entityTypeName === "PoHeader") {
      this.gotoNavPath(event, "/purchase-orders", issue.entityId);
    } else if (issue.entityTypeName === "JoDetail") {
      const jod = await this.dbQueryService.getJoDetail(+issue.entityId);
      if (jod != null) {
        this.gotoNavPath(event, "/in-process/manage/job-orders", jod.joHeaderId);
      }
    } else if (issue.entityTypeName === "PoDetail") {
      const pod = await this.dbQueryService.getPoDetail(+issue.entityId);
      if (pod != null) {
        this.gotoNavPath(event, "/purchase-orders", pod.poHeaderId);
      }
    } else if (issue.entityTypeName === "InvoiceHeader") {
      this.router.navigate(["/printable-invoice", issue.entityId]);
    } else if (issue.entityTypeName === "Manifest") {
      this.gotoManifest(event, issue.entityId);
    } else {
      return await this.dialogService.showOkMessage(
        "Not yet implemented",
        `Navigation to this type of issue has not yet been implemented`
      );
    }
  }

  private gotoNavPath(event, navPath: string, id: any) {
    const fm = AgFns.addFilterClause({}, "id", "number", id);
    const urlSuffix = encodeURIComponent(JSON.stringify(fm));
    const params = {
      queryParams: {
        fm: urlSuffix,
        key: id.toString(),
      },
    };
    NavFns.navigateWithClickEvent(event, this.router, navPath, params);
  }

  private gotoManifest(event, proximityManifestId: string) {
    const fm = AgFns.addFilterClause(
      {},
      "proximityManifestId",
      "text",
      proximityManifestId
    );
    const urlSuffix = encodeURIComponent(JSON.stringify(fm));
    const params = {
      queryParams: {
        fm: urlSuffix,
        key: proximityManifestId,
      },
    };
    NavFns.navigateWithClickEvent(event, this.router, "/manifests", params);
  }

  @wrapBusy
  async removeIssues() {
    const selectedEntityIssues = this.eiGridOptions.apiX.getSelectedRows();
    if (selectedEntityIssues.length === 0) {
      await this.dialogService.showOkMessage(
        "Nothing was selected",
        "No Issues were selected."
      );
      return;
    }
    const ok = await this.dialogService.yesNoDialog(
      `Delete ${selectedEntityIssues.length} Issue(s)?`,
      "Are you sure?"
    );
    if (!ok) return;
    for (const issue of selectedEntityIssues) {
      issue.entityAspect.setDeleted();
      await this.removeIssueFromNote(issue);
    }
    await this.dbSaveService.saveChanges();
    this.entityIssues = await this.dbQueryService.getEntityIssues();
    this.messageBus.notify({ id: MessageTypeEnum.EntityIssuesChanged });
  }

  async removeIssueFromNote(issue: EntityIssue) {
    const typeName = issue.entityTypeName;
    let note: PoNote | JoNote;
    if (typeName === "JoHeader" || typeName === "JoDetail") {
      note = await this.dbQueryService.findJoNoteForIssue(issue);
    } else if (typeName === "PoDetail") {
      // note = await this.dbQueryService.findPoNoteForIssue(issue);
    }
    if (note != null) {
      note.isIssue = false;
    }
  }
}
